import { trpc } from "@/lib/trpc";
import { firstCharacter } from "@papier/shared";
import { getCdnUrl } from "@papier/shared/utils/cdn-utils";
import { Avatar, AvatarFallback, AvatarImage } from "@papier/ui/components/ui/avatar";
import { Card, CardContent, CardHeader, CardTitle } from "@papier/ui/components/ui/card";
import { Separator } from "@papier/ui/components/ui/separator";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { formatDate, formatDistanceToNowStrict } from "date-fns";
import { Calendar, Clock, FileText, Globe } from "lucide-react";

export const Route = createFileRoute("/_app/projects/$projectSlug/_layout/about")({
  component: RouteComponent,
});

function RouteComponent() {
  const params = Route.useParams();
  const { data: project } = useSuspenseQuery(trpc.projects.getBySlug.queryOptions(params));
  const imageUrl = getCdnUrl(project.image);

  return (
    <main>
      <section>
        <div className="container mx-auto px-6 py-8">
          <div className="flex items-center gap-6">
            <Avatar className="size-25">
              <AvatarImage src={imageUrl} alt={project.name} />
              <AvatarFallback className="text-2xl capitalize">
                {firstCharacter(project.name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <div className="mb-2 flex items-center gap-3">
                <h1 className="text-balance text-3xl font-bold">{project.name}</h1>
              </div>
              <p className="text-muted-foreground mb-4 text-pretty">{project.description}</p>
              <div className="text-muted-foreground flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <Calendar className="size-4" />
                  Created {formatDistanceToNowStrict(project.createdAt, { addSuffix: true })}
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="size-4" />
                  <span>
                    Last updated {formatDistanceToNowStrict(project.updatedAt, { addSuffix: true })}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-6 py-8">
        <div className="grid gap-6 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Project Stats</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-muted/50 flex items-center justify-between rounded-lg p-3">
                <div className="flex items-center gap-3">
                  <div className="bg-primary/10 rounded-lg p-2">
                    <FileText className="text-primary size-4" />
                  </div>
                  <div>
                    <p className="text-muted-foreground text-sm">Published Templates</p>
                    <p className="text-2xl font-bold">{project.templatePublishedCount}</p>
                  </div>
                </div>
              </div>

              <div className="bg-muted/50 flex items-center justify-between rounded-lg p-3">
                <div className="flex items-center gap-3">
                  <div className="rounded-lg bg-blue-500/10 p-2">
                    <Globe className="size-4 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-muted-foreground text-sm">Environments</p>
                    <p className="text-2xl font-bold">{project.environments.length}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Other Information</CardTitle>
            </CardHeader>
            <CardContent className="grid flex-1 content-center gap-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Created by</span>
                <span className="font-medium">
                  {project.createdBy?.name || "Unknown"} ({project.createdBy?.email || "Unknown"})
                </span>
              </div>
              <Separator />
              <div className="flex justify-between">
                <span className="text-muted-foreground">Created on</span>
                <span className="font-medium">{formatDate(project.createdAt, "MMM d, yyyy")}</span>
              </div>
              <Separator />
              <div className="flex justify-between">
                <span className="text-muted-foreground">Last updated</span>
                <span className="font-medium">{formatDate(project.updatedAt, "MMM d, yyyy")}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </main>
  );
}
